import placeholderImg from '../../img/placeholders/city-placeholder.jpg';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import {
  IMPERIAL_BEACH_SAMPLE,
  PLACE_ID_COMPONENT_SEPARATOR,
  STATE_CODES,
} from '@/constants';
import { CITY, COUNTY } from '@/user/user-profile/user-profile-constants';
import { License } from '../license/license-types';

export const svgToDataURL = (svgStr) => {
  const encoded = encodeURIComponent(svgStr)
    .replace(/'/g, '%27')
    .replace(/"/g, '%22');

  const header = 'data:image/svg+xml,';
  const dataUrl = header + encoded;

  return dataUrl;
};

function getSum(total, num) {
  return total + Math.round(num);
}

export function sortDatasetsBySum(datasets) {
  // Apply insertion sort (applicable for 6 points of sort)
  const unsortedDatasets = datasets;
  const len = datasets.length;
  for (let i = 0; i < len; i++) {
    const tmp = unsortedDatasets[i];
    let j;
    // eslint-disable-next-line max-len
    for (
      j = i - 1;
      j >= 0 &&
      unsortedDatasets[j].data.reduce(getSum, 0) > tmp.data.reduce(getSum, 0);
      j--
    ) {
      unsortedDatasets[j + 1] = unsortedDatasets[j];
    }
    unsortedDatasets[j + 1] = tmp;
  }
  return unsortedDatasets;
}

/**
 * get the latest kpi data from dataPoints
 * and add it to the propertyData itself to make it a part of propertyData
 *
 * ASSUMPTION: latestKpi is the last data point
 *
 * @param {*} propertyData one property data
 */
export const getPropertyDataWithLatestKpi = (propertyData) => {
  const { dataPoints } = propertyData;
  const latestKpi = dataPoints[dataPoints.length - 1]; // assumption: latestKpi is the last data pt
  return { latestKpi, ...propertyData };
};

export function removeDuplicatePlatforms(urlInfoArray) {
  return uniqBy(urlInfoArray, 'platform');
}

export function detectDefaultOnlinePlatform(imageUrls) {
  if (isEmpty(imageUrls)) return null;
  const vendors = Object.keys(imageUrls);
  for (let i = 0; i < vendors.length; i++) {
    const v = vendors[i];
    if (!isEmpty(imageUrls[v])) return v;
  }
  return vendors[0];
}

export function getPropertyPlatforms(onlinePlatformsUrls) {
  if (!onlinePlatformsUrls) return null;

  return onlinePlatformsUrls.map((urlObject) => urlObject.platform).sort();
}

/**
 * Returns the first image from the first platform, from propertyList.imageUrls.
 * "First" image, platform are determined by index in their respective arrays
 *
 * @param {Array} imageUrls image urls from property list
 */
export function firstImageUrl(imageUrls) {
  const firstPlatform = detectDefaultOnlinePlatform(imageUrls);

  if (!firstPlatform) return placeholderImg;
  return imageUrls[firstPlatform][0];
}

/*
 * Returns all kpi's for the last month
 *
 * ATTENTION:
 * even if data does have dataPoints,
 * the 3 keys, adr, revenue, occupancy could still be missing!!!!!
 */
export function latestKpiGetter({ data }) {
  const emptyKpiData = { adr: {}, revenue: {}, occupancy: {} };
  /*
   *  `!data` protects against possible bug in ag-grid. Data may be null while table is loading data
   *  returning mock, empty KPIs reflecting a "no data" kpi state
   *  for every value obtained from dataPoints, we should have an entry in the object below
   *  `data && !data.dataPoints` protects against there being no datapoints for a given property
   */
  if (data?.dataPoints) {
    return { ...emptyKpiData, ...data.dataPoints[data.dataPoints.length - 1] };
  }
  return emptyKpiData;
}

export function getIndividualPropertyData(deckardid, propertyList) {
  const propertyData = propertyList.find(
    (data) => data.deckardid === deckardid
  );
  return getPropertyDataWithLatestKpi(propertyData);
}

export function isPropertyIdentified(property) {
  return Boolean(property?.apn);
}

/**
 * @typedef {Object} PlaceClassification
 * @property {string | undefined} state State abbreviation (uppercase letters, e.g. 'CA')
 * @property {'CITY' | 'COUNTY'} placeType Whether the place is a city or a county
 */
/**
 * Detects a place's state and whether it's a city or a county from
 * the its place ID string
 * @param {string} placeId - ID string of the place (e.g. `ca-san_mateo`)
 * @returns {PlaceClassification} An object that contains the state and type of the place
 */
export function getPlaceStateAndCityClassification(placeId) {
  if (placeId === IMPERIAL_BEACH_SAMPLE) {
    return {
      state: STATE_CODES.CALIFORNIA,
      placeType: CITY,
    };
  }
  const [state /* county part is skipped */, , cityOptional] =
    placeId?.split(PLACE_ID_COMPONENT_SEPARATOR) || [];

  const isCity = Boolean(cityOptional);

  return {
    state: state?.toUpperCase(),
    placeType: isCity ? CITY : COUNTY,
  };
}

// Creates a predicate on License which checks if license number matches given value.
function byLicenseNumber(value: string) {
  return (l: License) => l.certificateInfo?.licenseNumber === value;
}

// Creates a predicate on License which check if license ID (internal PK) matches given value.
function byLicenseId(value: number) {
  return (l: License) => l.licenseId === value;
}

export const getLicenseStatus = (
  licenseId: number | null,
  licenses: License[]
): string => {
  if (!licenseId) {
    return 'Unknown';
  }

  const license = licenses.find(byLicenseId(licenseId));
  if (license) {
    return `${license.status} License`;
  }

  return 'Active License';
};
